<template>
  <modal
    name="summary-treatment-modal"
    class="summary-treatment-modal"
    :classes="['modal-window']"
    :click-to-close="false"
    transition="scale"
    :width="800"
    height="auto"
  >
    <div class="modal-container">
      <div class="summary-treatment">
        <header class="summary-treatment__header flex--space-between--center">
          <span class="summary-treatment__header-text">
            {{ $t('dashboard.label.createTreatmentTab') }}
          </span>
          <div class="icon-close custom-icon" @click="closeModal"></div>
        </header>
        <summary-treatment />
        <footer class="summary-treatment__footer flex--center--center">
          <button
            v-if="isAlmaTerritory"
            class="rounded-button-orange rounded-button-orange--filled"
            :class="{
              'rounded-button-orange__alma rounded-button-orange__alma--filled': isAlmaTerritory
            }"
            @click="onNextButtonClick"
          >
            {{ $t('dashboard.action.next') }}
          </button>
          <button v-else class="otherXS cancel-button" @click="closeModal">
            {{ $t('dashboard.action.cancel') }}
          </button>
        </footer>
      </div>
    </div>
  </modal>
</template>

<script>
/* eslint-disable */

import { mapActions, mapGetters } from 'vuex';
import { isMobileDevice } from '@/modules/dashboard/utils';
import { get } from 'lodash';
import { getTreatmentType } from '@/modules/dashboard/utils/create-treatment-utils';
import { createTreatmentPreview, getLoyaltyProgramParticipants } from '@/modules/dashboard/api';
import treatmentPreviewMixin from '@/modules/dashboard/components/mixins/treatmentPreviewMixin';
import SummaryTreatment from '@/modules/dashboard/components/create-treatment-tab/SummaryTreatment';
import isAlmaTerritories from '@/modules/dashboard/components/mixins/isAlmaTerritories';

import { ALMA_TERRITORIES } from '@/modules/dashboard/constants/alma-countries';

import { types as dashTypes } from '@/modules/dashboard/store/types';
import rootTypes from '@/store/types';

export default {
  name: 'SummaryTreatmentModal',
  components: { SummaryTreatment },
  mixins: [treatmentPreviewMixin, isAlmaTerritories],

  data() {
    return {
      participantsSelectOptions: [],
      currentParticipant: null
    };
  },
  mounted() {
    this.initLoyaltyProgramSelectValues();
  },

  computed: {
    ...mapGetters({
      userId: rootTypes.getters.GET_USER_ID,
      currentPatientId: dashTypes.getters.CURRENT_PATIENT_ID,
      patientLocale: dashTypes.getters.CURRENT_PATIENT_LOCALE,
      treatmentId: dashTypes.getters.SUMMARY_TREATMENT_ID,
      summaryTreatmentId: dashTypes.getters.SUMMARY_TREATMENT_ID,
      recommendationDiagnostic: dashTypes.getters.RECOMMENDATION_DIAGNOSTIC,
      treatmentDirectoryRecommendations: dashTypes.getters.TREATMENT_DIRECTORY_RECOMMENDATIONS,
      doctorCountry: rootTypes.getters.GET_USER_COUNTRY
    }),
    isAlmaTerritory() {
      return ALMA_TERRITORIES.includes(this.doctorCountry);
    }
  },
  methods: {
    ...mapActions({
      setLoading: rootTypes.actions.SET_LOADING,
      addRequestError: dashTypes.actions.ADD_ERROR,
      setSummaryTreatmentId: dashTypes.actions.SET_SUMMARY_TREATMENT_ID
    }),
    async initLoyaltyProgramSelectValues() {
      const { data: participants } = await getLoyaltyProgramParticipants(this.userId);

      if (!participants) {
        return;
      }

      this.participantsSelectOptions = participants.map(({ name, id }) => ({
        label: name,
        value: id
      }));

      this.setDefaultParticipant();
    },
    setDefaultParticipant() {
      if (this.participantsSelectOptions.length > 1) {
        return;
      }

      const [participant] = this.participantsSelectOptions;

      this.currentParticipant = participant;
    },
    closeModal() {
      this.$modal.hide('summary-treatment-modal');
      this.$emit('after-modal-close', { cancel: true });
    },
    onNextButtonClick() {
      this.continueSummary();
    },
    async continueSummary() {
      await this.createTreatmentPreview();

      if (isMobileDevice()) {
        this.$router.push({
          name: 'ReviewPatientInviteTabs',
          params: {
            id: this.currentPatientId,
            templateType: REVIEW_TREATMENT_MESSAGE_TYPE.CREATE_TREATMENT
          }
        });

        return;
      }

      await new Promise(resolve => {
        this.$modal.hide('summary-treatment-modal');
        this.$nextTick(resolve);
      });

      await this.$nextTick();
      this.$modal.show('review-message-modal');
    },
    async createTreatmentPreview() {
      this.setLoading(true);

      try {
        const participantId = get(this.currentParticipant, 'value', null);
        const diagnosticId = get(this.recommendationDiagnostic, 'id', null);

        const type = getTreatmentType({
          isTreatmentDirectory: !!this.treatmentDirectoryRecommendations,
          diagnosticId
        });

        const treatmentDirectoryId = get(
          this.treatmentDirectoryRecommendations,
          'treatmentRecommendationId',
          null
        );

        const treatment = {
          ...this.getTreatmentForPreview(),
          paid: false,
          startDate: this.isCustomDateSelected && this.date ? this.date : new Date(),
          started: !this.isCustomDateSelected,
          patientId: this.currentPatientId,
          treatmentDirectoryId,
          participantId,
          isDraft: false,
          diagnosticId,
          type
        };

        const { data: treatmentId } = await createTreatmentPreview(
          this.currentPatientId,
          treatment
        );

        await this.setSummaryTreatmentId(treatmentId);
      } catch (error) {
        this.addRequestError('Treatment is not updated');
      } finally {
        this.setLoading(false);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/css/common/icons.css';
@import '../../../../assets/scss/helpers/colors';
@import '../../../../assets/scss/common/buttons';

.summary-treatment {
  &__header {
    height: 50px;
    padding: 0 20px;
    border-bottom: 0.5px solid $dim-white;
  }

  &__header-text {
    font-size: 15px;
    color: $text-color;
  }

  &__footer {
    height: 70px;
    border-top: 1px solid $dim-white;
  }
}

@media (max-width: 991px) {
  .summary-treatment-modal /deep/ .modal-window {
    max-width: 700px;
    max-height: 700px;
    margin: 0 auto;
    left: unset !important;
  }
}

@media (max-width: 991px) and (max-height: 750px) {
  .summary-treatment-modal /deep/ .modal-window {
    max-height: 100vh;
  }
}
</style>
